import {Select} from '@hconnect/common/components'
import {fromQuickSelectToCustomTaskRepetition} from '@hconnect/common/components/repetitiveTask'
import {
  CustomTaskRepetition,
  isCustomRepetition,
  QuickSelectRepetition,
  taskRepetitionUnits,
  ControlledFormField,
  FormFieldBase
} from '@hconnect/common/types'
import {useUpmPlantTimezone} from '@hconnect/upm'
import {Box, Stack, Tooltip} from '@mui/material'
import moment, {Moment} from 'moment-timezone'
import {useState} from 'react'
import {Controller, FieldValues, Path, useController} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../consts'

import {CheckBox} from './shared'
import {TaskRepetitionDialog} from './taskRepetition/TaskRepetitionDialog'

type Props<TField extends FieldValues> = FormFieldBase &
  ControlledFormField<TField> & {
    isRecurring?: boolean
    isTooltipDisabled?: boolean
    disableRecurringToggle?: boolean
    name: Path<TField>
    repetitionInfoName: Path<TField>
    onChange?: (value: boolean, repetitionInfo?: string) => void
    onDueDateChange: (dueDate: Moment) => void
    dataTestId?: string
    plantId: string
  }

export const TaskRepetition = <TField extends FieldValues>({
  control,
  disabled,
  readOnly,
  isRecurring,
  isTooltipDisabled,
  disableRecurringToggle,
  name,
  repetitionInfoName,
  onChange,
  onDueDateChange,
  plantId
}: Props<TField>) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const fieldDisabled = disabled || readOnly
  const checkboxDisabled = fieldDisabled || disableRecurringToggle
  const [isCustomDialog, setCustomDialog] = useState(false)
  const repetitionInfoController = useController({
    name: repetitionInfoName,
    control,
    disabled: fieldDisabled
  })

  const {onChange: onRepetitionInfoChange} = repetitionInfoController.field
  const timezone = useUpmPlantTimezone(plantId)

  return (
    <Stack direction="row" spacing={2} alignItems="center">
      <Controller
        name={name}
        control={control}
        render={({field}) => (
          <Tooltip
            title={isTooltipDisabled ? undefined : t('task.label.recurringDisabled')}
            arrow
            placement="bottom"
          >
            <Box>
              <CheckBox
                name={name}
                label={t('task.label.recurring')}
                value={field.value}
                disabled={checkboxDisabled}
                onChange={(_, checked) => {
                  field.onChange(checked)

                  // Set default repetition value when enabling recurring
                  if (checked) {
                    const defaultRepetition: QuickSelectRepetition = {
                      repetition: 'weekly'
                    }
                    onRepetitionInfoChange(defaultRepetition)
                  } else {
                    // Clear repetition when disabling recurring
                    onRepetitionInfoChange(undefined)
                  }

                  onChange?.(Boolean(checked))
                }}
              />
            </Box>
          </Tooltip>
        )}
      />

      {isRecurring && (
        <Controller
          name={repetitionInfoName}
          control={control}
          render={({field}) => {
            return (
              <>
                <Select
                  data-test-id="task-repetition-select"
                  name={repetitionInfoName}
                  label={t('task.label.recurring')}
                  value={
                    !field.value
                      ? undefined
                      : isCustomRepetition(field.value)
                        ? 'custom'
                        : (field.value as QuickSelectRepetition)?.repetition
                  }
                  options={[
                    ...taskRepetitionUnits.map((option) => ({
                      value: option as typeof field.value,
                      label: t(`task.label.${option}`)
                    })),
                    {
                      value: 'custom' as typeof field.value,
                      label: t('task.label.custom')
                    }
                  ]}
                  disabled={disabled || readOnly}
                  onChange={(value: string | undefined) => {
                    if (!value) return // Handle undefined case

                    if (value === 'custom') {
                      setCustomDialog(true)
                      // Keep the current repetitionInfo for custom dialog
                    } else {
                      // Create a QuickSelectRepetition object for non-custom values
                      const repetitionObject = {
                        repetition: value as 'daily' | 'weekly' | 'monthly' | 'yearly'
                      }
                      field.onChange(repetitionObject)
                    }
                    onChange?.(isRecurring, value)
                  }}
                  onOptionClick={(option) => {
                    if (!(disabled || readOnly) && option.value === 'custom') {
                      setCustomDialog(true)
                    }
                  }}
                />

                {isCustomDialog && (
                  <TaskRepetitionDialog
                    timezone={timezone}
                    customTaskRepetition={
                      isCustomRepetition(field.value)
                        ? field.value
                        : fromQuickSelectToCustomTaskRepetition(
                            field.value,
                            // Note: use end of the day for start date which is treated as dueDate
                            moment.tz(moment(), timezone).endOf('day').subtract(1, 'minute'),
                            // default to daily in custom
                            {repetition: 'daily'}
                          )
                    }
                    plantId={plantId}
                    onClose={() => setCustomDialog(false)}
                    onChange={(next: CustomTaskRepetition) => {
                      setCustomDialog(false)
                      onRepetitionInfoChange(next)
                      onDueDateChange(next.startDate)
                    }}
                  />
                )}
              </>
            )
          }}
        />
      )}
    </Stack>
  )
}
